import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { mocApi, draftStore } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CircleNotch, FloppyDisk, CheckCircle, Info, Warning, ShieldCheck, FileText, UsersThree } from '@phosphor-icons/react';

const STEPS = ['Identification', 'Situation & raison', 'Type & nature', 'Analyse guidée', 'Classification', 'Synthèse'];

// Real MOC_Types referential (5 fixed types). value sent to API = Type_ID; label = sheet Libelle (used by the assistant).
const MOC_TYPES = [
  { id: 'TYPE-01', label: 'Structure_Equipements', display: 'Structure / Équipements' },
  { id: 'TYPE-02', label: 'Parametres_Process_Instrumentation', display: 'Paramètres process / Instrumentation' },
  { id: 'TYPE-03', label: 'Matieres_Fluides', display: 'Matières / Fluides' },
  { id: 'TYPE-04', label: 'Produits', display: 'Produits' },
  { id: 'TYPE-05', label: 'Organisation', display: 'Organisation' },
];

const RAISONS = ['Performance', 'Sante_Securite', 'Réglementaire/Conformité', 'Environnement', 'Qualité', 'Fiabilité/Obsolescence', 'Réduction de coût', 'Autre'];
const NATURES = ['Permanent', 'Temporaire', 'Urgent'];

export default function NewMoc() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [localId] = useState(() => `draft_${Date.now()}`);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mocId, setMocId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [evalResult, setEvalResult] = useState(null);

  const [form, setForm] = useState({
    title: '', site: '', department: 'Procédé',
    currentSituation: '', futureSituation: '', raison: '',
    typeId: '', nature: 'Permanent', temporaryEnd: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const typeObj = MOC_TYPES.find((t) => t.id === form.typeId);

  useEffect(() => {
    const t = setTimeout(() => { if (form.title && !mocId) draftStore.save({ localId, ...form, requester: user?.email }); }, 800);
    return () => clearTimeout(t);
  }, [form, localId, user, mocId]);

  useEffect(() => {
    (async () => {
      const u = await mocApi.getCurrentUser().catch(() => null);
      const isNational = (u?.roles || []).some((r) => (r.Role_ID || r.role_id) === 'ROLE-12');
      let arr = [];
      // National admin sees all sites; regular users only their assigned site(s).
      if (isNational) arr = (await mocApi.listSites()) || [];
      if (!arr.length) { const s = await mocApi.getUserSites(); arr = Array.isArray(s) ? s : []; }
      if (!arr.length && u) {
        const seen = new Set(); const derived = [];
        const push = (id) => { if (id && !seen.has(id)) { seen.add(id); derived.push({ Site_ID: id, Nom_Site: id }); } };
        push(u.Site_Principal_ID);
        (u.roles || []).forEach((r) => push(r.Site_ID || r.site_id));
        arr = derived;
      }
      setSites(arr);
      if (arr.length && !form.site) set('site', arr[0].Site_ID || arr[0].id);
      setSitesLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Questions shown in the guided assistant: CRIT-01 + Oui/Non questions relevant to the selected type.
  const visibleQuestions = useMemo(() => {
    if (!questions.length) return [];
    return questions.filter((q) => {
      if (q.Actif === false) return false;
      if (q.Critere_ID === 'CRIT-02' || q.Critere_ID === 'CRIT-03') return false; // set from previous steps
      const assoc = q.Type_Modification_Associe;
      if (!assoc) return true;
      return typeObj && assoc === typeObj.label;
    });
  }, [questions, typeObj]);

  const canNext = () => {
    if (step === 0) return form.title.trim().length > 3 && !!form.site;
    if (step === 1) return !!form.currentSituation && !!form.futureSituation && !!form.raison;
    if (step === 2) return !!form.typeId && !!form.nature && (form.nature !== 'Temporaire' || !!form.temporaryEnd);
    if (step === 3) return visibleQuestions.every((q) => answers[q.Critere_ID]);
    return true;
  };

  const createDraftAndLoad = async () => {
    setCreating(true);
    const res = await mocApi.createMoc({
      title: form.title, site: form.site, department: form.department,
      currentSituation: form.currentSituation, futureSituation: form.futureSituation,
      raison: form.raison, nature: form.nature, temporaryEnd: form.temporaryEnd,
      typeId: form.typeId, requester: user?.email,
    });
    if (!res.ok) { setCreating(false); toast.error(res.error || 'Échec de création du brouillon'); return; }
    setMocId(res.id);
    draftStore.remove(localId);
    // Pre-answer the two list criteria captured in earlier steps.
    if (typeObj) mocApi.saveAssistantResponse(res.id, 'CRIT-02', typeObj.label);
    mocApi.saveAssistantResponse(res.id, 'CRIT-03', form.nature);
    setAnswers((a) => ({ ...a, 'CRIT-02': typeObj?.label, 'CRIT-03': form.nature }));
    try {
      const qs = await mocApi.getAssistantQuestions();
      setQuestions(Array.isArray(qs) ? qs : []);
    } catch { setQuestions([]); }
    setCreating(false);
    setStep(3);
    toast.success(`Brouillon créé : ${res.id}`);
  };

  const answerQuestion = (qid, val) => {
    setAnswers((a) => ({ ...a, [qid]: val }));
    if (mocId) mocApi.saveAssistantResponse(mocId, qid, val);
  };

  const runEvaluate = async () => {
    setEvaluating(true);
    const r = await mocApi.evaluateAssistant(mocId);
    setEvalResult(r);
    setEvaluating(false);
    setStep(4);
  };

  const handleNext = async () => {
    if (step === 2 && !mocId) return createDraftAndLoad();
    if (step === 3) return runEvaluate();
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    const r = await mocApi.submitMoc(mocId);
    setSubmitting(false);
    if (r.ok) { toast.success('MOC soumise pour évaluation'); navigate(`/moc/${mocId}`); }
    else toast.error(r.error || 'Échec de la soumission');
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6" data-testid="new-moc-page">
      <div className="lg:col-span-1">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Assistant guidé</div>
        <h1 className="font-display text-2xl font-black tracking-tight mt-1 mb-5">Nouvelle MOC</h1>
        <ol className="space-y-1">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm ${i === step ? 'bg-primary/10 text-primary font-semibold' : i < step ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 ${i < step ? 'bg-emerald-500 border-emerald-500 text-white' : i === step ? 'border-primary' : 'border-border'}`}>
                {i < step ? <CheckCircle size={14} weight="fill" /> : i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>
        {mocId
          ? <div className="mt-4 flex items-center gap-1.5 text-xs text-emerald-600 font-medium"><CheckCircle size={14} weight="fill" />Brouillon {mocId}</div>
          : <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><FloppyDisk size={14} />Brouillon enregistré localement</div>}
      </div>

      <div className="lg:col-span-3">
        <Card className="rounded-sm p-6 min-h-[440px]">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Étape {step + 1} / {STEPS.length}</div>
          <h2 className="font-display text-xl font-bold mt-1 mb-5">{STEPS[step]}</h2>

          {step === 0 && (
            <div className="space-y-4">
              <Field label="Titre de la modification">
                <Input data-testid="wizard-title" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Ex : Remplacement du convoyeur ligne 2" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Site">
                  <Select value={form.site} onValueChange={(v) => set('site', v)}>
                    <SelectTrigger data-testid="wizard-site" className="h-9"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => <SelectItem key={s.Site_ID || s.id} value={s.Site_ID || s.id}>{s.Nom_Site || s.Nom || s.Site_ID || s.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!sitesLoading && sites.length === 0 && (
                    <p data-testid="no-site-message" className="text-xs text-amber-700 dark:text-amber-400 mt-1.5">
                      Aucun site ne vous est attribué. Demandez à l'administrateur de vous affecter un rôle sur un site dans la feuille « Utilisateurs_Roles ».
                    </p>
                  )}
                </Field>
                <Field label="Service / Département"><Input data-testid="wizard-dept" value={form.department} onChange={(e) => set('department', e.target.value)} /></Field>
              </div>
              <Field label="Demandeur"><Input value={user?.email || ''} disabled /></Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <Field label="Situation actuelle"><Textarea data-testid="wizard-current" value={form.currentSituation} onChange={(e) => set('currentSituation', e.target.value)} className="min-h-[100px]" placeholder="Décrivez l'état existant" /></Field>
              <Field label="Situation souhaitée (description du changement)"><Textarea data-testid="wizard-future" value={form.futureSituation} onChange={(e) => set('futureSituation', e.target.value)} className="min-h-[100px]" placeholder="Décrivez le changement visé" /></Field>
              <Field label="Raison du changement">
                <Select value={form.raison} onValueChange={(v) => set('raison', v)}>
                  <SelectTrigger data-testid="wizard-raison" className="h-9"><SelectValue placeholder="Sélectionner une raison" /></SelectTrigger>
                  <SelectContent>
                    {RAISONS.map((r) => <SelectItem key={r} value={r} data-testid={`raison-opt-${r}`}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <Field label="Type de modification">
                <div className="grid sm:grid-cols-2 gap-2">
                  {MOC_TYPES.map((t) => (
                    <label key={t.id} data-testid={`wizard-type-${t.id}`} className={`flex items-center gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${form.typeId === t.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                      <input type="radio" name="type" className="accent-primary" checked={form.typeId === t.id} onChange={() => set('typeId', t.id)} />
                      <span className="text-sm font-medium">{t.display}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Nature du changement">
                <RadioGroup value={form.nature} onValueChange={(v) => set('nature', v)} className="space-y-2">
                  {NATURES.map((n) => (
                    <label key={n} data-testid={`wizard-nature-${n}`} className={`flex items-center gap-3 border rounded-sm p-3 cursor-pointer ${form.nature === n ? 'border-primary bg-primary/5' : 'border-border'}`}>
                      <RadioGroupItem value={n} />
                      <span className="text-sm font-medium">{n}</span>
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              {form.nature === 'Temporaire' && (
                <Field label="Date de fin prévue"><Input type="date" data-testid="wizard-temp-end" value={form.temporaryEnd} onChange={(e) => set('temporaryEnd', e.target.value)} /></Field>
              )}
              {form.nature === 'Urgent' && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-sm p-3">
                  <Warning size={16} className="shrink-0 mt-0.5" />Les MOC urgentes sont plafonnées en catégorie 1 et déclenchent une régularisation (J+1) — appliqué par le serveur.
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-assistant">
              {creating && <div className="flex items-center gap-2 text-sm text-muted-foreground"><CircleNotch size={16} className="animate-spin" />Préparation de l'analyse…</div>}
              <p className="text-sm text-muted-foreground">Répondez aux critères ci-dessous. La classification est calculée par le moteur de règles du serveur.</p>
              {visibleQuestions.some((q) => q.Critere_ID === 'CRIT-01') && (
                <QuestionCard q={visibleQuestions.find((x) => x.Critere_ID === 'CRIT-01')} value={answers['CRIT-01']} onChange={answerQuestion} options={['Nouveau', 'Modification']} />
              )}
              {visibleQuestions.filter((q) => q.Type_Reponse === 'Oui_Non').map((q) => (
                <QuestionCard key={q.Critere_ID} q={q} value={answers[q.Critere_ID]} onChange={answerQuestion} options={['Oui', 'Non']} />
              ))}
            </div>
          )}

          {step === 4 && (
            <div data-testid="wizard-result">
              {evaluating ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><CircleNotch size={18} className="animate-spin" />Évaluation en cours…</div>
              ) : evalResult ? <EvaluationView r={evalResult} /> : (
                <div className="text-sm text-muted-foreground py-10 text-center">Évaluation indisponible. Vous pouvez tout de même soumettre la MOC.</div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <Row k="Titre" v={form.title} />
              <Row k="Site / Service" v={`${(sites.find((s) => (s.Site_ID || s.id) === form.site)?.Nom_Site) || form.site} · ${form.department}`} />
              <Row k="Raison" v={form.raison} />
              <Row k="Type" v={typeObj?.display} />
              <Row k="Nature" v={form.nature + (form.nature === 'Temporaire' ? ` (fin ${form.temporaryEnd || '—'})` : '')} />
              <Row k="Catégorie proposée" v={evalResult ? `Catégorie ${evalResult.categorieProposee}` : '—'} />
              <Row k="PSSR requise" v={evalResult ? (evalResult.pssrObligatoire ? 'Oui' : 'Non') : '—'} />
              <div className="pt-3">
                <Button data-testid="wizard-submit" className="w-full h-11" disabled={submitting || !mocId} onClick={submit}>
                  {submitting ? <><CircleNotch size={18} className="animate-spin mr-2" />Soumission…</> : 'Soumettre la MOC pour évaluation'}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">La confirmation de la catégorie sera réalisée par le Pilote MOC.</p>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-4 border-t border-border">
            <Button variant="outline" data-testid="wizard-prev" disabled={step === 0 || step >= 3} onClick={() => setStep((s) => s - 1)}>Précédent</Button>
            {step < 5 && (
              <Button data-testid="wizard-next" disabled={!canNext() || creating || evaluating} onClick={handleNext}>
                {(creating || evaluating) ? <CircleNotch size={16} className="animate-spin" /> : (step === 2 ? 'Créer et analyser' : step === 3 ? 'Évaluer' : 'Suivant')}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>);
const Row = ({ k, v }) => (<div className="flex justify-between gap-4 border-b border-border py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v || '—'}</span></div>);

const QuestionCard = ({ q, value, onChange, options }) => (
  <div data-testid={`assistant-q-${q.Critere_ID}`} className="border border-border rounded-sm p-4">
    <p className="text-sm font-medium mb-3">{q.Libelle_Question}</p>
    <RadioGroup value={value || ''} onValueChange={(v) => onChange(q.Critere_ID, v)} className="flex flex-wrap gap-6">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value={opt} data-testid={`assistant-${q.Critere_ID}-${opt}`} /><span className="text-sm">{opt}</span>
        </label>
      ))}
    </RadioGroup>
  </div>
);

const catColor = (c) => c === 1 ? 'bg-red-600' : c === 2 ? 'bg-amber-500' : c === 0 ? 'bg-slate-400' : 'bg-slate-500';
const EvaluationView = ({ r }) => (
  <div className="space-y-5">
    <div className="text-center">
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Classification proposée par le moteur de règles</div>
      <div className={`mt-4 inline-flex items-center justify-center w-24 h-24 rounded-sm text-white font-black text-3xl ${catColor(r.categorieProposee)}`} data-testid="eval-category">
        Cat.{r.categorieProposee}
      </div>
      {r.categorieMax != null && r.categorieMax !== r.categorieProposee && (
        <p className="mt-2 text-xs text-amber-600">Plafonnée à la catégorie {r.categorieMax}.</p>
      )}
      {r.nature && <p className="mt-1 text-sm text-muted-foreground">Nature : <b>{r.nature}</b>{r.typeModification ? ` · ${r.typeModification}` : ''}</p>}
    </div>

    <div className="grid sm:grid-cols-2 gap-3">
      <InfoBlock icon={<ShieldCheck size={16} />} title="PSSR" testid="eval-pssr">{r.pssrObligatoire ? 'Obligatoire' : 'Non requise'}</InfoBlock>
      <InfoBlock icon={<UsersThree size={16} />} title="Approbateurs requis" testid="eval-approvers">{(r.approbateursRequis || []).join(', ') || '—'}</InfoBlock>
      <InfoBlock icon={<FileText size={16} />} title="Documents requis" testid="eval-docs">{(r.documentsRequis || []).join(', ') || '—'}</InfoBlock>
      <InfoBlock icon={<UsersThree size={16} />} title="Participants analyse de risque" testid="eval-participants">{(r.participantsAnalyseRisque || []).join(', ') || '—'}</InfoBlock>
    </div>

    {(r.alertes || []).length > 0 && (
      <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-sm p-3" data-testid="eval-alertes">
        <Warning size={16} className="shrink-0 mt-0.5" /><div><b>Alertes :</b> {r.alertes.join(', ')}</div>
      </div>
    )}
    {(r.blocages || []).length > 0 && (
      <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-sm p-3" data-testid="eval-blocages">
        <Info size={16} className="shrink-0 mt-0.5" /><div><b>Blocages :</b> {r.blocages.join(', ')}</div>
      </div>
    )}
  </div>
);

const InfoBlock = ({ icon, title, children, testid }) => (
  <div className="border border-border rounded-sm p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground font-semibold">{icon}{title}</div>
    <div className="text-sm font-medium mt-1">{children}</div>
  </div>
);
